import type { CapitalNeed } from '#/graphql/types';
import { getAsset, getAssetList } from '#/graphql/queries';
import { CAPITAL_NEED_TYPES, CAPITAL_NEED_STATUSES } from '../utils/constants';
import { gql } from '@apollo/client';
import FieldDefs from '#/types/FieldDefs';
import { getBaseColumns } from '#/common/utils/baseColumns';
import { color } from '@nuvolo/nuux-theme/utils';

const fields = {
  ...getBaseColumns(),
  id: {
    dataField: 'id',
    label: 'ID',
    editorOptions: { readOnly: true },
  },
  displayNumber: {
    dataField: 'displayNumber',
    label: 'Number',
    editorOptions: { readOnly: true, placeholder: 'Auto generated' },
  },
  title: {
    dataField: 'title',
    label: 'Title',
    isRequired: true,
    editorOptions: {
      maxLength: 1024,
      validationRules: [
        {
          type: 'required',
          message: 'Title is required',
        },
      ],
    },
  },
  type: {
    dataField: 'type',
    label: 'Type',
    editorType: 'SelectBox',
    editorOptions: {
      items: CAPITAL_NEED_TYPES,
      displayExpr: 'label',
      valueExpr: 'value',
    },
  },
  status: {
    dataField: 'status',
    label: 'Status',
    editorType: 'SelectBox',
    editorOptions: {
      items: CAPITAL_NEED_STATUSES,
      displayExpr: 'label',
      valueExpr: 'value',
    },
  },
  plannedDate: {
    dataField: 'plannedDate',
    label: 'Planned Date',
    editorType: 'DateBox',
    editorOptions: {
      type: 'date',
      placeholder: 'Select date',
    },
  },
  cost: {
    dataField: 'cost',
    label: 'Planned Cost',
    editorType: 'Currency',
    editorOptions: {
      placeholder: 'Enter amount',
      min: 0,
      step: 0.01,
      format: {
        type: 'currency',
        precision: 2,
      },
      showSpinButtons: true,
    },
  },
  description: {
    dataField: 'description',
    label: 'Description',
    editorType: 'HtmlEditor',
    editorOptions: {
      height: 100,
      placeholder: 'Enter description',
      maxLength: 8192,
    },
  },
  assetId: {
    dataField: 'assetId',
    label: 'Related Asset',
    isRequired: true,
    editorType: 'RelatedItemsCards',
    editorOptions: {
      placeholder: 'Search assets...',
      displayExpr: 'assetTag',
      valueExpr: 'id',
      dropdownColumns: 1,
      button: {
        tooltip: 'View asset details',
        url: '/assets/:value/details',
        desktopOnly: false,
      },
      dataStoreOptions: {
        getItemList: gql(getAssetList),
        getItem: gql(getAsset),
        filter: ['state', 'notcontains', 'retired'],
      },
      cardConfig: {
        iconName: 'cube',
        iconColor: color('custom', 'constantTeal'),
        iconHolderOpacity: 0.2,
        subtitleExpr: 'model.name',
      },
    },
  },
} satisfies FieldDefs<CapitalNeed>;

export default fields;
